import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Rss, Link2, RefreshCw, Trash2, CheckCircle2, AlertTriangle, Clock } from "lucide-react";

export default function FeedSyncCard() {
    const [feed, setFeed] = useState(null); // {} = nessun feed, null = loading
    const [url, setUrl] = useState("");
    const [busy, setBusy] = useState(false);
    const [feedback, setFeedback] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get("/agency/feed");
            setFeed(data && data.id ? data : {});
        } catch (e) {
            setFeed({});
        }
    };

    useEffect(() => { load(); }, []);

    const connect = async (e) => {
        e?.preventDefault?.();
        if (!url.trim()) return;
        setBusy(true); setFeedback(null);
        try {
            const { data } = await api.post("/agency/feed", { url: url.trim() });
            setFeed(data);
            setUrl("");
            setFeedback({ type: "success", msg: `Feed collegato: ${data.sync?.added || 0} annunci importati, ${data.sync?.updated || 0} aggiornati.` });
        } catch (err) {
            setFeedback({ type: "error", msg: err?.response?.data?.detail || "Impossibile validare il feed" });
        } finally { setBusy(false); }
    };

    const sync = async () => {
        setBusy(true); setFeedback(null);
        try {
            const { data } = await api.post("/agency/feed/sync");
            setFeed(data);
            setFeedback({ type: "success", msg: `Sincronizzato: ${data.sync?.added || 0} nuovi, ${data.sync?.updated || 0} aggiornati, ${data.sync?.deactivated || 0} rimossi.` });
        } catch (err) {
            setFeedback({ type: "error", msg: err?.response?.data?.detail || "Errore durante la sincronizzazione" });
        } finally { setBusy(false); }
    };

    const disconnect = async () => {
        if (!window.confirm("Disconnettere il feed? Gli annunci già importati verranno messi in pausa.")) return;
        setBusy(true); setFeedback(null);
        try {
            await api.delete("/agency/feed");
            setFeed({});
            setFeedback({ type: "success", msg: "Feed disconnesso." });
        } catch (err) {
            setFeedback({ type: "error", msg: err?.response?.data?.detail || "Errore" });
        } finally { setBusy(false); }
    };

    if (feed === null) {
        return (
            <div data-testid="feed-sync-card-loading" className="mb-16 border border-white/10 bg-ink-900/30 p-6 lg:p-8 animate-pulse">
                <div className="h-5 w-48 bg-white/10 mb-3" />
                <div className="h-4 w-full max-w-md bg-white/5" />
            </div>
        );
    }
    const connected = !!feed.id;

    return (
        <div data-testid="feed-sync-card" className="mb-16 border border-white/10 bg-ink-900/30 p-6 lg:p-8 shadow-[inset_2px_2px_8px_rgba(0,0,0,0.35),inset_-2px_-2px_8px_rgba(255,255,255,0.03)]">
            <div className="flex items-start gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center border border-white/20 text-gold shrink-0">
                    <Rss size={20} />
                </div>
                <div>
                    <span className="label-eyebrow">Sincronizzazione Automatica</span>
                    <h2 className="font-serif text-2xl sm:text-3xl text-ink-50 mt-1">Centro Sincro Feed</h2>
                    <p className="text-xs text-ink-400 mt-1 max-w-xl">
                        Collega il feed XML/CSV del tuo gestionale: gli annunci verranno importati e aggiornati automaticamente ogni 12 ore (illimitati, non contano sul limite manuale).
                    </p>
                </div>
            </div>

            {!connected ? (
                <form onSubmit={connect} className="flex flex-wrap gap-2">
                    <input
                        data-testid="feed-url-input"
                        type="url"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        placeholder="https://tuogestionale.it/export/feed.xml"
                        required
                        className="flex-1 min-w-[260px] bg-ink-950/60 border border-white/15 focus:border-gold outline-none px-4 py-2.5 text-sm"
                    />
                    <button data-testid="feed-connect-btn" type="submit" disabled={busy || !url.trim()} className="cta-gold px-5 py-2.5 text-xs disabled:opacity-50 flex items-center gap-2">
                        <Link2 size={12} /> {busy ? "Verifica..." : "Collega feed"}
                    </button>
                </form>
            ) : (
                <div className="border border-white/10 bg-ink-900/50 p-5">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                            {feed.last_status === "error" ? (
                                <AlertTriangle size={16} className="text-red-400 shrink-0" />
                            ) : (
                                <CheckCircle2 size={16} className="text-gold shrink-0" />
                            )}
                            <span data-testid="feed-url-display" className="text-sm text-ink-100 truncate">{feed.url}</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <button data-testid="feed-sync-btn" onClick={sync} disabled={busy} className="px-3 py-1.5 text-xs border border-gold/30 text-gold hover:bg-gold/10 flex items-center gap-1">
                                <RefreshCw size={12} /> Sincronizza ora
                            </button>
                            <button data-testid="feed-disconnect-btn" onClick={disconnect} disabled={busy} className="px-3 py-1.5 text-xs border border-white/10 text-ink-400 hover:text-red-300 flex items-center gap-1">
                                <Trash2 size={12} /> Disconnetti
                            </button>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 mt-4 text-[11px] text-ink-400">
                        {feed.last_sync_at && (
                            <span className="flex items-center gap-1"><Clock size={11} /> Ultima sync: {new Date(feed.last_sync_at).toLocaleString("it-IT")}</span>
                        )}
                        <span>Formato: {feed.format?.toUpperCase() || "—"}</span>
                        <span>{feed.last_synced_count || 0} annunci nel feed</span>
                        {feed.last_status === "error" && feed.last_error && (
                            <span data-testid="feed-error-msg" className="text-red-300">{feed.last_error}</span>
                        )}
                    </div>
                </div>
            )}

            {feedback && (
                <div
                    data-testid={`feed-feedback-${feedback.type}`}
                    className={`mt-4 p-3 text-sm border ${feedback.type === "success" ? "border-green-500/40 bg-green-900/20 text-green-200" : "border-red-500/40 bg-red-900/20 text-red-200"}`}
                >
                    {feedback.msg}
                </div>
            )}
        </div>
    );
}
